// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';

// Material UI ( mui )  components
import Box from '@mui/material/Box';

// Section for import custom components --------------------------------------------------------------------------------
import { MansionMarketPriceHistoryContext } from '@providers/MansionDetail/MansionMarketPriceHistory/MansionMarketPriceHistoryProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import { MarketPriceHistoryItem } from '@/types/components/MansionDetail/MarketPriceHistoryPropsData';
import '@styles/mansions/detail.scss';

type ChartProps = {
  legendState: {
    series1: boolean;
    series2: boolean;
    series3: boolean;
    series4: boolean;
  };
  toggleLegendState: (seriesKey: keyof ChartProps['legendState']) => void;
  averageSquareMeter: number;
};

export default function Chart({ legendState, toggleLegendState, averageSquareMeter }: ChartProps) {
  const { marketPriceHistoryData } = useContext(MansionMarketPriceHistoryContext);
  let marketPriceHistories;
  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryMansions &&
    typeof marketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryCities &&
    typeof marketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryTowns &&
    typeof marketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryStations &&
    typeof marketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories;
  }

  let dates: string[] = [];
  if (marketPriceHistories && typeof marketPriceHistories === 'object') {
    dates = Object.keys(marketPriceHistories);
  }

  const getTsuboData = (dataObject: { [date: string]: MarketPriceHistoryItem }) => {
    // console.log(dataObject);
    return dates.map((date) => {
      if (!dataObject[date]) {
        // console.log('ERROR! dataObject[date]');
        // console.log(date);
        return 0;
      }
      // else {
      //   console.log('OK! dataObject[date]');
      //   console.log(date);
      //   console.log(dataObject[date]);
      // }
      const price = dataObject[date].pricePerTsubo ? dataObject[date].pricePerTsubo : 0;
      return price / 10000;
    });
  };

  // function getMinTsuboData(dataObject: { [date: string]: MarketPriceHistoryItem }) {
  //   const prices = Object.values(dataObject).map((item) => item.pricePerTsubo);
  //   // 価格の配列から最小値を見つける
  //   return Math.min(...prices);
  // }

  // 各TuboDataから最小値を取得
  // const minTsuboDataArray = [];
  // if (marketPriceHistoryData.marketPriceHistoryMansions) {
  //   minTsuboDataArray.push(getMinTsuboData(marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories));
  // }
  // if (marketPriceHistoryData.marketPriceHistoryCities) {
  //   minTsuboDataArray.push(getMinTsuboData(marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories));
  // }
  // if (marketPriceHistoryData.marketPriceHistoryTowns) {
  //   minTsuboDataArray.push(getMinTsuboData(marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories));
  // }
  // if (marketPriceHistoryData.marketPriceHistoryStations) {
  //   minTsuboDataArray.push(getMinTsuboData(marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories));
  // }

  // 4つの最小値から全体の最小値を取得
  // const minAmongAll = minTsuboDataArray.length > 0 ? Math.min(...minTsuboDataArray) : 0;

  // let tickInterval = 1;
  // let min = 0;

  // if (minAmongAll >= 10000 && minAmongAll < 200000) {
  //   tickInterval = 5;
  //   min = 0;
  // } else if (minAmongAll >= 200000 && minAmongAll < 500000) {
  //   tickInterval = 5;
  //   min = 20;
  // } else if (minAmongAll >= 500000 && minAmongAll < 1000000) {
  //   tickInterval = 20;
  //   min = 50;
  // } else if (minAmongAll >= 1000000 && minAmongAll < 3000000) {
  //   tickInterval = 50;
  //   min = 100;
  // } else if (minAmongAll >= 3000000 && minAmongAll < 5000000) {
  //   tickInterval = 50;
  //   min = 200;
  // } else if (minAmongAll >= 5000000 && minAmongAll < 10000000) {
  //   tickInterval = 100;
  //   min = 400;
  // } else if (minAmongAll >= 10000000) {
  //   tickInterval = 100;
  //   min = 900;
  // }

  const series = [];
  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryMansions &&
    typeof marketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories === 'object') {
      series.push({
        name: `赤線 = ${marketPriceHistoryData.marketPriceHistoryMansions.mansionName}の売買相場`,
        className: 'mansions',
        visible: legendState.series1,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories),
        lineWidth: 4,
        color: '#FF3C00',
        zIndex: 4,
        events: {
          legendItemClick() {
            toggleLegendState('series1');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryCities &&
    typeof marketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories === 'object') {
      series.push({
        name: `青線 = ${marketPriceHistoryData.marketPriceHistoryCities.cityName}の売買相場`,
        className: 'cities',
        visible: legendState.series2,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories),
        lineWidth: 4,
        color: '#00B4FF',
        dashStyle: 'ShortDot',
        zIndex: 3,
        events: {
          legendItemClick() {
            toggleLegendState('series2');
            return false;
          },
        },
      });
    }
  }
  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryTowns &&
    typeof marketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories === 'object') {
      series.push({
        name: `紫線 = ${marketPriceHistoryData.marketPriceHistoryTowns.townName}の売買相場`,
        className: 'towns',
        visible: legendState.series3,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories),
        lineWidth: 4,
        color: '#C800FF',
        dashStyle: 'ShortDot',
        zIndex: 2,
        events: {
          legendItemClick() {
            toggleLegendState('series3');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryStations &&
    typeof marketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories === 'object') {
      series.push({
        name: `緑線 = ${marketPriceHistoryData.marketPriceHistoryStations.stationName}の売買相場`,
        className: 'stations',
        visible: legendState.series4,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories),
        lineWidth: 4,
        color: '#37C80A',
        dashStyle: 'ShortDot',
        zIndex: 1,
        events: {
          legendItemClick() {
            toggleLegendState('series4');
            return false;
          },
        },
      });
    }
  }

  const options = {
    chart: {
      height: 500,
      type: 'line',
      spacing: [0, 0, 0, 0], // チャート要素の余白を調整
      style: {
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'", // ここでグローバルなフォントファミリーを設定
      },
    },
    tooltip: {
      enabled: true, // tooltipを有効にする
      shared: false, // 共有tooltipを有効にする（複数のシリーズがある場合に便利）
      useHTML: true, // HTMLタグをtooltip内で使用可能にする
      style: {
        color: '#191919',
        backgroundColor: '#FFF9AC',
        fontSize: '12px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      formatter() {
        // この関数内で `this` はtooltipを表示しているポイントを指します。
        const { point }: any = this; // 現在のポイントにアクセス
        const xAxisLabel: string = point.category; // x軸のラベル（年月）を取得
        const squareMeter: number = (point.y / 3.30579) * averageSquareMeter; // y軸の値を坪数に変換
        // tooltipのHTMLをカスタマイズ
        return `<b>${xAxisLabel}<br/>${squareMeter.toFixed(0)}万円<br>(${point.y.toFixed(0)}万円/坪)</b>`;
      },
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: dates.map((date) => format(new Date(date), 'yyyy年MM月')),
      labels: {
        rotation: 0,
        style: {
          fontSize: '12px',
          fontFamily:
            "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
        },
      },
    },
    yAxis: [
      {
        title: {
          text: '',
        },
        // min,
        // tickInterval,
        labels: {
          formatter(): string {
            // 価格を坪数に変換（1坪 = 3.30579平方メートル）
            const context: any = this;
            const squareMeter = (context.value / 3.30579) * averageSquareMeter;
            // ラベルのフォーマットを定義
            return `${squareMeter.toFixed(0)}万円<br><span style="font-size: 11px;">(${context.value.toFixed(
              0,
            )}万円/坪)</span>`;
          },
          style: {
            fontSize: '12px',
            fontFamily:
              "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
          },
        },
        opposite: false,
      },
    ],
    legend: {
      itemStyle: {
        fontSize: '14px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
      borderWidth: 0,
      margin: 0, // 凡例のマージンを設定
      padding: 24, // 凡例内のパディングを設定
      itemWidth: 400,
      itemMarginTop: 8, // 判例項目間の上の余白を調整
      itemMarginBottom: 8, // 判例項目間の下の余白を調整
      x: 30, // 凡例を右に移動
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: true,
      },
    },
    series,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 878, // チャート要素の幅が878px以下の場合
          },
          chartOptions: {
            xAxis: {
              labels: {
                rotation: 300, // ここでラベルの回転を変更
              },
            },
          },
        },
      ],
    },
  };

  return (
    <Box className="w-full">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}
